import { useState } from 'react';
import { Color, Size, correctionChangeText, fill, formatDateOnly, t, timeCorrectionAnswerPath, timeCorrectionAnswerProblems, type TimeCorrectionRow, type TimeCorrectionsSection } from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { whenText } from '../../shell/viewerDates';
import { post } from '../records/api';
import { line } from '../settings/SettingsShell';
import styles from './HrPages.module.css';

/** One correction's second line: the answer kept, or that none is yet. The employee reads it as theirs, the chain as the employee's. */
function answerText(row: TimeCorrectionRow, own: boolean, zone: string, format: ReturnType<typeof useDateFormat>): string {
  if (row.answer === null || row.answeredAt === null) return t('hr.timeCorrection.unanswered');
  const when = whenText(row.answeredAt, zone, format);
  if (row.answer === 'Agreed') return fill(t(own ? 'hr.timeCorrection.agreed' : 'hr.timeCorrection.agreedBy'), { when });
  return fill(t(own ? 'hr.timeCorrection.disputed' : 'hr.timeCorrection.disputedBy'), { when, reason: row.disputeReason ?? '' });
}

/** The dispute's window, through the Form frame: why, in the employee's own words. The hours do not change. */
function DisputeWindow({ apiBaseUrl, row, onClose, onAnswered }: { apiBaseUrl: string; row: TimeCorrectionRow; onClose: () => void; onAnswered: () => void }) {
  const format = useDateFormat();
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const problems = timeCorrectionAnswerProblems({ answer: 'Disputed', reason });
  const send = async () => {
    const answer = await post<unknown>(timeCorrectionAnswerPath(row.id), { answer: 'Disputed', reason }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onAnswered();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.timeCorrection.dispute')}
      subtitle={line(formatDateOnly(`${row.workDate}T00:00:00.000Z`, format), '·', `${row.hoursBefore} → ${row.hoursAfter}`)}
      dataForm="time-correction-dispute"
      typed={reason !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.timeCorrection.send'), onAct: () => send(), disabled: problems.length > 0, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-time-correction-dispute={row.id}>
        <Textarea size={Size.Large} label={t('hr.timeCorrection.reasonLabel')} value={reason} onChange={setReason} placeholder={t('hr.timeCorrection.reasonHint')} required name="timeCorrectionReason" />
      </div>
    </FormFrame>
  );
}

/**
 * The corrections to a job's time under the Time section (piece 34; decision 12930; T3855): the hours before and
 * after, who corrected them and why, with Agree and Dispute where the employee may still answer, and the kept
 * answer or the words that none is yet. 🔒 The managers above the job read it with no buttons.
 */
export function TimeCorrections({ apiBaseUrl = '', employmentId, section, own, onAnswered }: {
  apiBaseUrl?: string;
  employmentId: string;
  section: TimeCorrectionsSection;
  own: boolean;
  onAnswered: () => void;
}) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const [busy, setBusy] = useState(false);
  const [disputing, setDisputing] = useState<TimeCorrectionRow | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  if (section.rows.length === 0) return null;
  const agree = async (row: TimeCorrectionRow) => {
    setBusy(true);
    setProblem(null);
    const answer = await post<unknown>(timeCorrectionAnswerPath(row.id), { answer: 'Agreed' }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setProblem(answer.message);
    onAnswered();
  };
  return (
    <div className={`${styles.fields} ${styles.corrections}`} data-time-corrections={employmentId}>
      <h3 className={styles.meta}>{t('hr.timeCorrection.title')}</h3>
      {problem ? <p className={styles.meta} role="alert" data-time-correction-problem="true">{problem}</p> : null}
      <ul className={styles.list}>
        {section.rows.map((row) => (
          <li key={row.id} className={styles.item} data-time-correction={row.id} data-time-correction-answer={row.answer ?? 'none'}>
            <span className={styles.itemMain}>
              <span>{fill(t('hr.timeCorrection.line'), { date: formatDateOnly(`${row.workDate}T00:00:00.000Z`, format), before: row.hoursBefore, after: row.hoursAfter, change: correctionChangeText(row.hoursBefore, row.hoursAfter), name: row.correctedByName })}</span>
              {row.reason ? <span className={styles.meta}>{row.reason}</span> : null}
              <span className={styles.meta} data-time-correction-state="true">{answerText(row, own, zone, format)}</span>
            </span>
            {row.mayAnswer ? (
              <span className={styles.controls}>
                <span data-time-correction-agree="true">
                  <Button size={Size.Small} color={Color.Good} text={[t('hr.timeCorrection.agree')]} disabled={busy} actions={[() => void agree(row)]} />
                </span>
                <span data-time-correction-dispute-open="true">
                  <Button size={Size.Small} color={Color.Warning} text={[t('hr.timeCorrection.dispute')]} disabled={busy} actions={[() => { setProblem(null); setDisputing(row); }]} />
                </span>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {/* 🔨 Mounted only while open and keyed to the correction, so a second one never shows the first one's draft. */}
      {disputing ? (
        <DisputeWindow
          key={disputing.id}
          apiBaseUrl={apiBaseUrl}
          row={disputing}
          onClose={() => setDisputing(null)}
          onAnswered={() => {
            setDisputing(null);
            onAnswered();
          }}
        />
      ) : null}
    </div>
  );
}
